/**
 * FavoritesPanel — UI for managing saved addresses
 * 
 * Opens as a slide-up panel from the menu.
 * Shows list of favorites with edit/delete. Add new via form.
 * Tap a favorite to set it as origin or destination.
 */

import { favoritesService } from '../services/FavoritesService.js';
import { autocompleteService } from '../services/AutocompleteService.js';
import { w, currentMarket } from '../services/market.js';

const PANEL_STYLES = `
.favorites-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.5);
    z-index: 9999;
    display: flex;
    align-items: flex-end;
    justify-content: center;
}
.favorites-panel {
    width: 100%;
    max-width: 480px;
    max-height: 80vh;
    background: white;
    border-radius: 16px 16px 0 0;
    overflow-y: auto;
    padding: 20px 20px calc(20px + env(safe-area-inset-bottom, 0px)) 20px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
.favorites-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}
.favorites-header h3 {
    margin: 0;
    font-size: 18px;
    color: #2c3e50;
}
.favorites-close {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #7f8c8d;
    padding: 4px 8px;
}
.favorites-list {
    list-style: none;
    padding: 0;
    margin: 0 0 16px;
}
.favorites-item {
    display: flex;
    align-items: center;
    padding: 12px;
    border-bottom: 1px solid #f0f0f0;
    gap: 12px;
}
.favorites-item:last-child {
    border-bottom: none;
}
.favorites-item-icon {
    font-size: 20px;
    width: 32px;
    text-align: center;
}
.favorites-item-info {
    flex: 1;
    min-width: 0;
}
.favorites-item-label {
    font-weight: 600;
    font-size: 14px;
    color: #2c3e50;
}
.favorites-item-address {
    font-size: 12px;
    color: #7f8c8d;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.favorites-item-actions {
    display: flex;
    gap: 8px;
}
.favorites-item-btn {
    background: none;
    border: none;
    font-size: 16px;
    cursor: pointer;
    padding: 4px;
    opacity: 0.6;
}
.favorites-item-btn:hover {
    opacity: 1;
}
.favorites-use-btns {
    display: flex;
    gap: 4px;
}
.favorites-use-btn {
    background: #e3f2fd;
    border: none;
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 11px;
    color: #1e88e5;
    cursor: pointer;
    font-weight: 500;
}
.favorites-use-btn:hover {
    background: #bbdefb;
}
.favorites-add-form {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 12px;
}
.favorites-add-form input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 14px;
    margin-bottom: 8px;
    box-sizing: border-box;
}
.favorites-add-form button {
    width: 100%;
    padding: 10px;
    background: #F5A623;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
}
.favorites-add-form button:disabled {
    background: #ccc;
    cursor: not-allowed;
}
.favorites-empty {
    text-align: center;
    padding: 24px;
    color: #7f8c8d;
    font-size: 14px;
}
`;

export function openFavoritesPanel() {
    injectStyles();

    const overlay = document.createElement('div');
    overlay.className = 'favorites-overlay';
    overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };

    const panel = document.createElement('div');
    panel.className = 'favorites-panel';
    panel.onclick = (e) => e.stopPropagation();

    renderPanel(panel, overlay);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    // Close the mobile dropdown menu
    const dropdown = document.getElementById('mobileDropdown');
    if (dropdown) dropdown.style.display = 'none';
}

function renderPanel(panel, overlay) {
    const favorites = favoritesService.getAll();

    let listHtml = '';
    if (favorites.length === 0) {
        listHtml = `<div class="favorites-empty">${w('No favorites yet. Add your home address or frequent destinations below.')}</div>`;
    } else {
        listHtml = '<ul class="favorites-list">' + favorites.map(f => `
            <li class="favorites-item" data-id="${f.id}">
                <span class="favorites-item-icon">${getIcon(f.label)}</span>
                <div class="favorites-item-info">
                    <div class="favorites-item-label">${f.label}</div>
                    <div class="favorites-item-address">${f.address || `${f.lat.toFixed(4)}, ${f.lng.toFixed(4)}`}</div>
                </div>
                <div class="favorites-use-btns">
                    <button class="favorites-use-btn" data-action="origin" data-id="${f.id}">From</button>
                    <button class="favorites-use-btn" data-action="destination" data-id="${f.id}">To</button>
                </div>
                <div class="favorites-item-actions">
                    <button class="favorites-item-btn" data-action="delete" data-id="${f.id}">🗑️</button>
                </div>
            </li>
        `).join('') + '</ul>';
    }

    panel.innerHTML = `
        <div class="favorites-header">
            <h3>⭐ Favorites</h3>
            <button class="favorites-close" onclick="this.closest('.favorites-overlay').remove()">×</button>
        </div>
        ${listHtml}
        <div class="favorites-add-form">
            <input type="text" id="favLabelInput" placeholder="Label (e.g. Home, Work, Mom's)" maxlength="50">
            <input type="text" id="favAddressInput" placeholder="Address (start typing...)" maxlength="200">
            <input type="hidden" id="favLatInput">
            <input type="hidden" id="favLngInput">
            <button id="favAddBtn" disabled>Add Favorite</button>
        </div>
    `;

    // Wire "From" and "To" buttons
    panel.querySelectorAll('.favorites-use-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const id = btn.dataset.id;
            const action = btn.dataset.action;
            const fav = favorites.find(f => f.id === id);
            if (!fav) return;

            const inputId = action === 'origin' ? 'start' : 'destination';
            const input = document.getElementById(inputId);
            if (input) {
                input.value = fav.address || fav.label;
                // Set resolved place directly
                if (window.autocompleteService) {
                    window.autocompleteService.resolvedPlaces[inputId] = {
                        lat: fav.lat,
                        lng: fav.lng,
                        formattedAddress: fav.address || fav.label,
                        source: 'favorite'
                    };
                }
            }
            overlay.remove();
        });
    });

    // Wire delete buttons
    panel.querySelectorAll('[data-action="delete"]').forEach(btn => {
        btn.addEventListener('click', async () => {
            const id = btn.dataset.id;
            await favoritesService.delete(id);
            renderPanel(panel, overlay);
        });
    });

    // Wire the add form — use autocomplete on the address field
    const addressInput = panel.querySelector('#favAddressInput');
    const latInput = panel.querySelector('#favLatInput');
    const lngInput = panel.querySelector('#favLngInput');
    const addBtn = panel.querySelector('#favAddBtn');
    const labelInput = panel.querySelector('#favLabelInput');

    // Simple autocomplete for the address input in the form — show dropdown, don't auto-fill
    let addTimeout;
    addressInput.addEventListener('input', () => {
        clearTimeout(addTimeout);
        latInput.value = '';
        lngInput.value = '';
        addBtn.disabled = true;

        addTimeout = setTimeout(async () => {
            const val = addressInput.value;
            if (val.length < 3) {
                removeFavDropdown(addressInput);
                return;
            }
            try {
                const API_URL = import.meta.env.VITE_BACKEND_URL || 'https://6wdv4xnpei.execute-api.us-west-1.amazonaws.com/prod';
                const resp = await fetch(`${API_URL}/api/autocomplete?input=${encodeURIComponent(val)}&market=${currentMarket()}`);
                const data = await resp.json();
                if (data.status === 'success' && data.suggestions.length > 0) {
                    showFavDropdown(addressInput, data.suggestions, async (suggestion) => {
                        addressInput.value = suggestion.description;
                        removeFavDropdown(addressInput);
                        // Resolve coordinates
                        const placeResp = await fetch(`${API_URL}/api/place?placeId=${encodeURIComponent(suggestion.placeId)}`);
                        const placeData = await placeResp.json();
                        if (placeData.status === 'success') {
                            latInput.value = placeData.place.lat;
                            lngInput.value = placeData.place.lng;
                            addBtn.disabled = !labelInput.value.trim();
                        }
                    });
                } else {
                    removeFavDropdown(addressInput);
                }
            } catch (e) { removeFavDropdown(addressInput); }
        }, 400);
    });

    addressInput.addEventListener('blur', () => {
        setTimeout(() => removeFavDropdown(addressInput), 200);
    });

    labelInput.addEventListener('input', () => {
        addBtn.disabled = !(labelInput.value.trim() && latInput.value);
    });

    addBtn.addEventListener('click', async () => {
        const label = labelInput.value.trim();
        const lat = parseFloat(latInput.value);
        const lng = parseFloat(lngInput.value);
        const address = addressInput.value.trim();

        if (!label || isNaN(lat) || isNaN(lng)) {
            alert('Debug: label=' + label + ' lat=' + latInput.value + ' lng=' + lngInput.value);
            return;
        }

        addBtn.disabled = true;
        addBtn.textContent = 'Saving...';
        const result = await favoritesService.add(label, lat, lng, address);
        if (!result) {
            alert('Save failed — check if signed in');
            addBtn.disabled = false;
            addBtn.textContent = w('Add Favorite');
            return;
        }
        renderPanel(panel, overlay);
    });
}

function getIcon(label) {
    const l = label.toLowerCase();
    if (l === 'home') return '🏠';
    if (l === 'work') return '💼';
    if (l.includes('mom') || l.includes('dad') || l.includes('parent')) return '👨‍👩‍👧';
    if (l.includes('camp') || l.includes('rv')) return '🏕️';
    return '📍';
}

function injectStyles() {
    if (document.getElementById('favorites-panel-styles')) return;
    const style = document.createElement('style');
    style.id = 'favorites-panel-styles';
    style.textContent = PANEL_STYLES;
    document.head.appendChild(style);
}

// Expose globally for the menu button
window.openFavoritesPanel = openFavoritesPanel;


function showFavDropdown(input, suggestions, onSelect) {
    removeFavDropdown(input);
    const dropdown = document.createElement('div');
    dropdown.className = 'autocomplete-dropdown';
    dropdown.id = 'fav-address-dropdown';
    dropdown.style.display = 'block';
    dropdown.style.position = 'absolute';
    dropdown.style.left = '0';
    dropdown.style.right = '0';
    dropdown.style.top = input.offsetTop + input.offsetHeight + 'px';
    dropdown.style.zIndex = '10001';
    dropdown.style.background = 'white';
    dropdown.style.borderRadius = '6px';
    dropdown.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
    dropdown.style.maxHeight = '200px';
    dropdown.style.overflowY = 'auto';

    suggestions.forEach(s => {
        const item = document.createElement('div');
        item.className = 'autocomplete-item';
        item.style.padding = '10px 12px';
        item.style.cursor = 'pointer';
        item.style.borderBottom = '1px solid #f0f0f0';
        item.innerHTML = `<span style="font-size:14px;font-weight:500;">${s.mainText}</span><br><span style="font-size:12px;color:#7f8c8d;">${s.secondaryText}</span>`;
        item.addEventListener('mousedown', (e) => {
            e.preventDefault();
            onSelect(s);
        });
        dropdown.appendChild(item);
    });

    input.parentElement.style.position = 'relative';
    input.parentElement.appendChild(dropdown);
}

function removeFavDropdown(input) {
    const existing = document.getElementById('fav-address-dropdown');
    if (existing) existing.remove();
}
